import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import Technologies from '../../widgets/Technologies';
import { Text } from '../../widgets/Text';

import * as css from './CaseStudy.m.css';
import * as commonCss from '../../Common.m.css';
import * as summaryCss from '../../widgets/Summary.m.css';
import ContentWrapper from '../../widgets/ContentWrapper';
import { Image } from '../../widgets/Image';
import ImageSection from '../../widgets/ImageSection';
import Section from '../../widgets/Section';
import SocialCaseStudyList, { rvLifeBlurb } from './SocialCaseStudyList';

const skRvLifeMockup = require('../../assets/images/work-mockups/sk-rv-life-mockup.png');
const skRvLifeMockupHighRes = require('../../assets/images/work-mockups/sk-rv-life-mockup@2x.png');

const factory = create();

export const CaseStudySocialRVLife = factory(function CaseStudySocialRVLife() {
	return (
		<div classes={css.root}>
			<Head
				title="Online Community Platform App Modernization Case Study | SitePen"
				description="SitePen modernized a mobile application for Social Knowledge which greatly improved the code quality and maintainability of the codebase."
				noindex
			/>

			<Hero background="level2RedOrange" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Social Knowledge',
					subtitle: 'Project: RV Life',
					blurb: (
						<virtual>
							<span>{rvLifeBlurb}</span>
							<Technologies
								icons={[
									'react',
									'react-native',
									'redux',
									'mobx',
									'typescript',
									'jest'
								]}
							/>
						</virtual>
					)
				}}
			</Hero>

			<Section>
				<ContentWrapper size="small">
					<Text type="section">The Situation</Text>
					<Text type="body">
						The Social Knowledge team builds RV Life with a mix of native and web
						technologies, leveraging their existing expertise while providing users with
						features only supported via native applications. Social Knowledge wanted to
						modernize the code structure and development process for their RV Life trip
						management application.
					</Text>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ImageSection contentBias>
					{{
						title: (
							<div classes={[css.stackBlurbNoGutter]}>
								<Text type="section">The Solution</Text>
							</div>
						),
						blurb: (
							<div>
								<Text type="body">
									SitePen provided technical expertise and development support to
									directly assist with modernization and give Social Knowledge new
									tools to improve code quality. This included:
								</Text>
								<ul classes={commonCss.list}>
									<li>
										<Text type="body">
											Upgrading foundational and supporting libraries used by
											the application including React Native to bring in
											performance improvements and bug fixes
										</Text>
									</li>
									<li>
										<Text type="body">
											Converting the application's JavaScript codebase to
											TypeScript and setting up automatic code validation to
											improve the development experience and the resulting
											code quality
										</Text>
									</li>
									<li>
										<Text type="body">
											Updating the application's React components to use
											modern, performance-first approaches through reduced
											indirection and increased memoization
										</Text>
									</li>
									<li>
										<Text type="body">
											Configuring an end-to-end testing infrastructure capable
											of both testing individual components and verifying
											complex user experiences
										</Text>
									</li>
									<li>
										<Text type="body">
											Providing documentation and support to Social Knowledge
											developers as they worked in tandem to complete
											additional identified improvements{' '}
										</Text>
									</li>
								</ul>
							</div>
						),
						images: {
							main: (
								<Image
									src={skRvLifeMockup}
									highResSrc={skRvLifeMockupHighRes}
									shadow="light"
								/>
							)
						}
					}}
				</ImageSection>
			</Section>
			<ContentWrapper>
				<div classes={css.result}>
					<Text type="section">The Result</Text>
					<p classes={[commonCss.paragraphWidth, summaryCss.blurb]}>
						SitePen modernized the RV Life codebase, upgraded the DevOps experience, and
						helped the Social Knowledge team immediately gain benefits from an
						accelerated upgrade. Social Knowledge's developers also gained a more
						in-depth understanding of React Native and TypeScript best practices.
						Together, these outcomes improved Social Knowledge’s effectiveness with RV
						Life’s ongoing development, allowing them to bring new features to market
						more efficiently.
					</p>
				</div>
			</ContentWrapper>
			<Section>
				<ContentWrapper>
					<SocialCaseStudyList caseStudies={['trip', 'complete']} />
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default CaseStudySocialRVLife;
